import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import { getStructureIndexByElementUuid } from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { ImageMask, MediaImage } from 'editor/src/store/design/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import applyToSelectedMediaTypeOperation from './applyToSelectedMediaTypeOperation';

/**
 * GAPI-61890: sets (or removes, with undefined) the mask shape of every selected image element.
 * Each image is compared on its own: an image that already has the requested shape keeps its mask (a rounded
 * rectangle keeps its radius). With `onlyShape`, only the images whose current mask has that shape are updated,
 * and those whose radius already matches are left alone. Nothing is dispatched (no undo step) when no image changes.
 */
const applyImageMaskToSelectionOperation =
  (mask: ImageMask | undefined, preventSavingState = false, onlyShape?: ImageMask['shape']) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const needsUpdate = (element: MediaImage) => {
      if (onlyShape) {
        return element.mask?.shape === onlyShape && element.mask?.radius !== mask?.radius;
      }
      return element.mask?.shape !== mask?.shape;
    };

    const state = getState();
    const anyChange = state.editor.selectedElementUuids.some((uuid) => {
      const address = getStructureIndexByElementUuid(state.design.designData, uuid);
      const element = address ? getMediaElement(state, address) : undefined;
      return element?.type === 'image' && needsUpdate(element);
    });
    if (!anyChange) {
      return;
    }

    dispatch(
      applyToSelectedMediaTypeOperation(
        'image',
        function applyImageMask(element: MediaImage): Partial<MediaImage> | undefined {
          return needsUpdate(element) ? { mask } : undefined;
        },
        preventSavingState,
      ),
    );
  };

export default applyImageMaskToSelectionOperation;
